import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import Description from "./Description"
import Detail from "./Detail"
import Gallery from "./Gallery"
import RelatedProduct from "./RelatedProduct"
import { CustomVariantItemProps, MinProductItemProps, ProductDetailItemProps, ProductVariantItemProps } from "./constants"
import { NextSeo, ProductJsonLd } from "next-seo"
import { useAttributesStore } from "@/stores/useAttributesStore"
import { AttributeValueItem } from "@/lib/constants"
import { Offers, Review } from "next-seo/lib/types"
import Rating from "./Rating"
import TabDetail from "./TabDetail"
import AddToCart from "./AddToCart"
import { useRecommendStore } from "@/stores/useRecommendStore"
import { useRouter } from "next/router"
import PwP from "./PwP"
import { useCartContext } from "@/context/CartContext"
import Cookies from "js-cookie"
import axios from "axios"
import { useUserStore } from "@/stores/useUserStore"
import { v4 as uuidv4 } from 'uuid'
import { useProductAttributes } from "@/hooks/useProductAttributes"


// 定義屬性狀態介面
interface AttributeSelections {
  readonly color: number;
  readonly size: number;
  readonly stage: number;
  readonly pattern: number;
  readonly gender: number;
  readonly flavour: number;
}

interface AttributeValues {
  readonly colors: ReadonlyArray<AttributeValueItem>;
  readonly sizes: ReadonlyArray<AttributeValueItem>;
  readonly stages: ReadonlyArray<AttributeValueItem>;
  readonly patterns: ReadonlyArray<AttributeValueItem>;
  readonly genders: ReadonlyArray<AttributeValueItem>;
  readonly flavours: ReadonlyArray<AttributeValueItem>;
}

const Product = (props: ProductDetailItemProps) => {
  const router = useRouter();
  const { showCart, setShowCart } = useCartContext();
  const attributesData = useAttributesStore(state => state.attributes);
  const updateRecommend = useRecommendStore(state => state.updateRecommend);
  const userData = useUserStore(state => state.userInfo);
  const updateUser = useUserStore(state => state.updateUser);
  const product_code = useRef(props.product_code);

  const [variantId, setVariantId] = useState(0);
  const [selectProduct, setSelectProduct] = useState<MinProductItemProps | null>(null);
  const [variant, setVariant] = useState<ProductVariantItemProps | null>(null);
  const [relatedVariant, setRelatedVariant] = useState<ProductVariantItemProps | null>(null);
  const [open, setOpen] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [firstVariant, setFirstVariant] = useState<ProductVariantItemProps | null>(null);
  const [init, setInit] = useState(true);

  const [selectedCustomVariant, setSelectedCustomVariant] = useState<CustomVariantItemProps | null>(null)
  const [fromName, setFromName] = useState<string>('')
  const [toName, setToName] = useState<string>('')
  const [toEmail, setToEmail] = useState<string>('')
  const [toMobile, setToMobile] = useState<string>('')
  const [toMessage, setToMessage] = useState<string>('')
  const [pregnancy, setPregnancy] = useState<string>('')
  const [duDate, setDuDate] = useState<Date | null>(null)
  const [childName, setChildName] = useState<string>('')
  const [childAge, setChildAge] = useState<number | null>(null)
  const [childGender, setChildGender] = useState<string>('')
  const [sharedSalesPrice, setSharedSalesPrice] = useState<number>(0)
  const [affiliate, setAffiliate] = useState<string | undefined>(undefined);
  const [clickAttribute, setClickAttribute] = useState<boolean>(false);
  const { 
    attributeValues,
    attributeSelections,
    updateAttributeSelection,
    updateAttributeValues,
    additionalImages,
    offerVariants,
    reviewList,
    parseAttributes
  } = useProductAttributes(props, attributesData);

  useEffect(() => {
    if (selectProduct !== null && !relatedOpen) {
      setRelatedOpen(true);
    }
  }, [selectProduct, relatedOpen])

  useEffect(() => {
    if (!relatedOpen) {
      setSelectProduct(null);
      setRelatedVariant(null);
    }
  }, [relatedOpen])

  // 初始化解析
  useEffect(() => {
    if (init) {
      parseAttributes().then(() => {
        // 等待屬性初始化後，檢查 matchingVariants
        setTimeout(() => {
          // 取得目前的 matchingVariants
          const selectedAttrs = [
            attributeSelections.color,
            attributeSelections.size,
            attributeSelections.stage,
            attributeSelections.pattern,
            attributeSelections.gender,
            attributeSelections.flavour
          ].filter(value => value !== 0).sort((a, b) => a - b);
          const matching = props.product_variant_list.filter(variant => {
            const sortedVariantAttrs = [...variant.attribute_value_list].sort((a, b) => a - b);
            return sortedVariantAttrs.toString() === selectedAttrs.toString();
          });
          if (matching.length === 0 && props.product_variant_list.length > 0) {
            // 自動選擇第一個變體並同步 attributeSelections
            const first = props.product_variant_list[0];
            setFirstVariant(first);
            setVariant(first);
            // 同步屬性選擇
            const attrMapping = new Map([
              ["color", attributeValues.colors],
              ["size", attributeValues.sizes],
              ["stage", attributeValues.stages],
              ["pattern", attributeValues.patterns],
              ["gender", attributeValues.genders],
              ["flavour", attributeValues.flavours],
            ]);
            first.attribute_value_list.forEach(attrId => {
              attrMapping.forEach((values, key) => {
                const foundValue = values.find(v => v.id === attrId);
                if (foundValue) {
                  updateAttributeSelection(key as keyof AttributeSelections, attrId);
                }
              });
            });
          }
        }, 0);
        setInit(false);
      });
    }
  }, [init, parseAttributes, attributeSelections, attributeValues, props.product_variant_list, updateAttributeSelection]);

  // 記憶化屬性映射表
  const attrMapping = useMemo(() => {
    const attributeKeys = Object.keys(attributeValues) as Array<keyof AttributeValues>;
    return new Map(
      attributeKeys.map(key => [
        key.slice(0, -1) as keyof AttributeSelections,
        attributeValues[key]
      ])
    );
  }, [attributeValues]);

  // 監聽變體選擇變化
  useEffect(() => {
    if (!variant) return;

    // 更新 variantId
    setVariantId(variant.variant_id);

    // 同步屬性選擇
    variant.attribute_value_list.forEach(attrId => {
      attrMapping.forEach((values, key) => {
        const foundValue = values.find(v => v.id === attrId);
        if (foundValue) {
          updateAttributeSelection(key, attrId);
        }
      });
    });
  }, [variant, attrMapping, updateAttributeSelection]);

  // 記憶化選中的屬性陣列
  const selectedAttrs = useMemo(() => {
    return [
      attributeSelections.color,
      attributeSelections.size,
      attributeSelections.stage,
      attributeSelections.pattern,
      attributeSelections.gender,
      attributeSelections.flavour
    ].filter(value => value !== 0)
     .sort((a, b) => a - b);
  }, [attributeSelections]);

  // 記憶化符合條件的變體
  const matchingVariants = useMemo(() => {
    return props.product_variant_list.filter(variant => {
      const sortedVariantAttrs = [...variant.attribute_value_list].sort((a, b) => a - b);
      return sortedVariantAttrs.toString() === selectedAttrs.toString();
    });
  }, [props.product_variant_list, selectedAttrs]);

  // 監聽屬性選擇變化
  useEffect(() => {
    if (matchingVariants.length > 0) {
      setFirstVariant(matchingVariants[0]);
      if (!variant) {
        setVariant(matchingVariants[0]);
      }
    }
  }, [matchingVariants, variant]);

  // 處理推薦商品更新
  useEffect(() => {
    if (refresh && props.slug) {
      setRefresh(false);
      
      if (props.our_pick?.[0]?.product_list?.length > 0) {
        updateRecommend({
          update_date: Date.now(),
          data: props.our_pick[0].product_list
        });
      }
    }
  }, [refresh, props.slug, props.our_pick, updateRecommend]);

  // 處理路由變更
  const handleRouteChange = useCallback(() => {
    setInit(true);
    setRefresh(true);
    setVariant(null);
    setVariantId(0);
    setSharedSalesPrice(0);
    setClickAttribute(false);
  }, []);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, handleRouteChange]);

  // 處理聯盟行銷
  const handleAffiliate = useCallback(async () => {
    const category = router.query.utm_source === "affiliate" && router.query.utm_medium
      ? window.btoa(`affiliate,${router.query.utm_medium}`)
      : window.btoa(`affiliate,${Cookies.get(window.btoa("affiliate")) || ""}`);

    try {
      let uuid = userData.uuid;
      if (!uuid) {
        uuid = uuidv4();
        const updatedUserData = { ...userData, uuid };
        updateUser(updatedUserData);
      }

      const response = await axios.get("/api/product", {
        headers: { Authorization: `Bearer ${userData.token}` },
        params: {
          category,
          utm_source: router.query.utm_source,
          utm_medium: router.query.utm_medium,
          utm_campaign: router.query.utm_campaign,
          utm_content: router.query.utm_content,
          uid: uuid,
          slug: router.query.slug,
        }
      });

      if (response.data.status === "ok" && response.data.data) {
        const data = JSON.parse(window.atob(response.data.data));
        if (data.shared_sales_price != null) {
          setSharedSalesPrice(data.shared_sales_price);
        }
      }
    } catch (error) {
      console.error('Affiliate API Error:', error);
    }
  }, [router.query, userData, updateUser]);

  useEffect(() => {
    const storedAffiliate = Cookies.get(window.btoa("affiliate"));
    setAffiliate(storedAffiliate);

    if ((router.query.utm_source === "affiliate" && router.query.utm_medium) || storedAffiliate) {
      if (router.query.utm_source === "affiliate" && router.query.utm_medium) {
        Cookies.set(
          window.btoa("affiliate"),
          router.query.utm_medium.toString(),
          { expires: 7, path: '/' }
        );
        setAffiliate(router.query.utm_medium.toString());
      }
      handleAffiliate();
    }
  }, [router.query, handleAffiliate]);

  return (
    <>
      <NextSeo
        title=''
        defaultTitle={props.meta.title}
        description={props.meta.description}
        canonical={process.env.NEXT_PUBLIC_HOSTNAME + "/product/" + props.slug}
        additionalMetaTags={[
          {
            name: "keywords",
            content: props.meta.keywords,
          },
        ]}
        openGraph={{
          images: [{
            url: props.image_gallery.length > 0 ? props.image_gallery[0].image : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/brand/logo-mooimom-middle.png",
            width: 800,
            height: 800,
            alt: props.name + " image",
          }]
        }}
      />
      <ProductJsonLd
        productName={props.name}
        images={props.image_gallery.length > 0 ? [props.image_gallery[0].image] : [process.env.NEXT_PUBLIC_STATIC_SITE + "/images/brand/logo-mooimom-middle.png"]}
        description={props.meta.description}
        brand={props.brand}
        sku={props.product_code}
        offers={offerVariants.length > 0 ? offerVariants : offerVariants[0]}
        additional_images={additionalImages.length > 0 ? additionalImages : undefined}
        {...(props.rating.counts > 0 && {
          aggregateRating: {
            ratingValue: props.rating.review_rating,
            reviewCount: props.rating.counts,
          },
          reviews: reviewList
        })}
      />
      <main className="grid grid-cols-1 gap-0 mb-8 md:container md:my-0 md:py-12 md:grid-cols-12 md:gap-6">
        <Gallery
          product_code={props.product_code}
          name={props.name}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          product_label_right_top={props.product_label_right_top}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          variantId={variantId}
          use_product_option={props.use_product_option}
          selectColor={attributeSelections.color}
          clickAttribute={clickAttribute}
        />
        <Detail
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          ranking={props.ranking}
          attribute_value_list={props.attribute_value_list}
          categories_list={props.categories_list}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          description_list={props.description_list}
          description_media_list={props.description_media_list}
          people_say={props.people_say}
          our_pick={props.our_pick}
          meta={props.meta}
          size_guide={props.size_guide}
          product_label_right_top={props.product_label_right_top}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          urls={props.urls}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
          setVariantId={setVariantId}
          rating={props.rating}
          promotion={props.promotion}
          selling_points={props.selling_points}
          how_to_use={props.how_to_use}
          pwp_name={props.pwp_name}
          pwp={props.pwp}
          setOpen={setOpen}
          variant={variant}
          setVariant={setVariant}
          firstVariant={firstVariant}
          virtual_product={props.virtual_product}
          need_shipping={props.need_shipping}
          custom_variant_list={props.custom_variant_list}
          video_list={props.video_list}
          use_product_option={props.use_product_option}
          selectedCustomVariant={selectedCustomVariant}
          setSelectedCustomVariant={setSelectedCustomVariant}
          fromName={fromName}
          setFromName={setFromName}
          toName={toName}
          setToName={setToName}
          toEmail={toEmail}
          setToEmail={setToEmail}
          toMobile={toMobile}
          setToMobile={setToMobile}
          toMessage={toMessage}
          setToMessage={setToMessage}
          pregnancy={pregnancy}
          setPregnancy={setPregnancy}
          duDate={duDate}
          setDuDate={setDuDate}
          childName={childName}
          setChildName={setChildName}
          childAge={childAge}
          setChildAge={setChildAge}
          childGender={childGender}
          setChildGender={setChildGender}
          sharedSalesPrice={sharedSalesPrice}
          affiliate={affiliate}
          setClickAttribute={setClickAttribute}
          setSelectColor={(value: number) => updateAttributeSelection("color", value)}
          compare_price={props.compare_price}
          sales_status={props.sales_status}
          userData={userData}
        />
      </main>
      {props.pwp.length > 0 && (
        <>
          <hr className="my-6 bg-reviewBg md:hidden" />
          <div className="container">
            <hr className="hidden mb-6 bg-reviewBg md:block" />
            <PwP pwp={props.pwp} pwp_name={props.pwp_name} setSelectProduct={setSelectProduct} />
          </div>
          <hr className="my-6 bg-reviewBg md:hidden" />
        </>
      )}
      {props.rating.counts > 0 && (
        <section className="mb-12 md:container md:mb-20" aria-label="product rating">
          <Rating rating={props.rating} slug={props.slug} />
        </section>
      )}
      <section className="mb-12 md:mb-20" aria-label="product description">
        <Description
          name={props.name}
          description_media_list={props.description_media_list}
        />
      </section>
      {props.our_pick.length > 0 && props.our_pick[0].product_list.length > 0 && (
        <section className="container hidden grid-cols-5 mb-20 md:grid" aria-label="related products">
          <div className={`col-span-3 pr-20 ${process.env.NEXT_PUBLIC_STYLE_OPTION === "A" ? "hidden" : ""}`}>
            <TabDetail details={props.description_list} how_to_use={props.how_to_use} />
          </div>
          <div className={process.env.NEXT_PUBLIC_STYLE_OPTION === "A" ? "col-span-5" : "col-span-2"}>
            <RelatedProduct
              our_pick={props.our_pick}
              setSelectProduct={setSelectProduct}
              selectedCustomVariant={selectedCustomVariant}
              setSelectedCustomVariant={setSelectedCustomVariant}
              fromName={fromName}
              setFromName={setFromName}
              toName={toName}
              setToName={setToName}
              toEmail={toEmail}
              setToEmail={setToEmail}
              toMobile={toMobile}
              setToMobile={setToMobile}
              toMessage={toMessage}
              setToMessage={setToMessage}
              pregnancy={pregnancy}
              setPregnancy={setPregnancy}
              duDate={duDate}
              setDuDate={setDuDate}
              childName={childName}
              setChildName={setChildName}
              childAge={childAge}
              setChildAge={setChildAge}
              childGender={childGender}
              setChildGender={setChildGender}
            />
          </div>
        </section>
      )}
      {props.our_pick.length > 0 && props.our_pick[0].product_list.length > 0 && (
        <section className="mb-12 md:hidden" aria-label="related products">
          <RelatedProduct
            our_pick={props.our_pick}
            setSelectProduct={setSelectProduct}
            selectedCustomVariant={selectedCustomVariant}
            setSelectedCustomVariant={setSelectedCustomVariant}
            fromName={fromName}
            setFromName={setFromName}
            toName={toName}
            setToName={setToName}
            toEmail={toEmail}
            setToEmail={setToEmail}
            toMobile={toMobile}
            setToMobile={setToMobile}
            toMessage={toMessage}
            setToMessage={setToMessage}
            pregnancy={pregnancy}
            setPregnancy={setPregnancy}
            duDate={duDate}
            setDuDate={setDuDate}
            childName={childName}
            setChildName={setChildName}
            childAge={childAge}
            setChildAge={setChildAge}
            childGender={childGender}
            setChildGender={setChildGender}
          />
        </section>
      )}
      {variant !== null && (
        <AddToCart
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          image={props.image_gallery[0].image}
          attribute_value_list={props.attribute_value_list}
          product_variant_list={props.product_variant_list}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
          style="popup"
          variant={variant}
          setVariant={setVariant}
          setShowCart={setShowCart}
          action="add"
          open={open}
          setOpen={setOpen}
          virtual_product={props.virtual_product}
          need_shipping={props.need_shipping}
          custom_variant_list={props.custom_variant_list}
          selectedCustomVariant={selectedCustomVariant}
          setSelectedCustomVariant={setSelectedCustomVariant}
          fromName={fromName}
          setFromName={setFromName}
          toName={toName}
          setToName={setToName}
          toEmail={toEmail}
          setToEmail={setToEmail}
          toMobile={toMobile}
          setToMobile={setToMobile}
          toMessage={toMessage}
          setToMessage={setToMessage}
          pregnancy={pregnancy}
          setPregnancy={setPregnancy}
          duDate={duDate}
          setDuDate={setDuDate}
          childName={childName}
          setChildName={setChildName}
          childAge={childAge}
          setChildAge={setChildAge}
          childGender={childGender}
          setChildGender={setChildGender}
          pwp_name={props.pwp_name}
          pwp={props.pwp}
          shared_sales_price={sharedSalesPrice}
          type={affiliate !== null && affiliate !== undefined ? "affiliate" : "ec"}
          use_product_option={props.use_product_option}
          pwp_product_id={props.pwp_product_id}
          pwp_price={props.pwp_price}
        />
      )}
      {selectProduct !== null && (
        <AddToCart
          id={selectProduct.id}
          name={selectProduct.name}
          msrp={selectProduct.msrp}
          sales_price={selectProduct.sales_price}
          product_code={selectProduct.sku}
          qty={selectProduct.qty}
          image={selectProduct.image}
          attribute_value_list={selectProduct.attribute_value_list}
          product_variant_list={selectProduct.product_variant_list}
          special_price={selectProduct.special_price}
          brand={selectProduct.brand}
          slug={selectProduct.slug}
          style="popup"
          variant={relatedVariant}
          setVariant={setRelatedVariant}
          setShowCart={setShowCart}
          action="add"
          open={relatedOpen}
          setOpen={setRelatedOpen}
          virtual_product={selectProduct.virtual_product}
          need_shipping={selectProduct.need_shipping}
          custom_variant_list={selectProduct.custom_variant_list}
          selectedCustomVariant={selectedCustomVariant}
          setSelectedCustomVariant={setSelectedCustomVariant}
          fromName={fromName}
          setFromName={setFromName}
          toName={toName}
          setToName={setToName}
          toEmail={toEmail}
          setToEmail={setToEmail}
          toMobile={toMobile}
          setToMobile={setToMobile}
          toMessage={toMessage}
          setToMessage={setToMessage}
          pregnancy={pregnancy}
          setPregnancy={setPregnancy}
          duDate={duDate}
          setDuDate={setDuDate}
          childName={childName}
          setChildName={setChildName}
          childAge={childAge}
          setChildAge={setChildAge}
          childGender={childGender}
          setChildGender={setChildGender}
          pwp_name={selectProduct.pwp_name}
          pwp={selectProduct.pwp}
          shared_sales_price={null}
          type={affiliate !== null && affiliate !== undefined ? "affiliate" : "ec"}
          pwp_product_id={selectProduct.pwp_product_id}
          pwp_price={selectProduct.pwp_price}
        />
      )}
    </>
  )
}

export default Product
